/**
 * Help / User Guide Page
 *
 * In-app documentation covering all features of the LiftOff system.
 */

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import './Help.css';

const sections = [
  { id: 'overview',       label: 'Overview' },
  { id: 'dashboard',      label: 'Dashboard' },
  { id: 'requests',       label: 'Creating Requests' },
  { id: 'request-types',  label: 'Request Types' },
  { id: 'products',       label: 'Products' },
  { id: 'hierarchy',      label: 'Product Hierarchy' },
  { id: 'reporting',      label: 'Reporting' },
  { id: 'export',         label: 'Exporting Data' },
  { id: 'admin',          label: 'Admin Features' },
  { id: 'status',         label: 'Status Workflow' },
  { id: 'cuc',            label: 'Capability Update Catalog' },
  { id: 'ppp',            label: 'Product Packaging & Pricing' },
  { id: 'privacy',        label: 'Privacy' },
  { id: 'salesops',       label: 'SalesOps Request' },
  { id: 'new-product-request', label: 'New Product Request' },
  { id: 'tech',           label: 'Technical Overview' },
  { id: 'db-seeding',    label: 'Database Seeding' },
];

const Help = () => {
  const history = useHistory();
  const [activeSection, setActiveSection] = useState('overview');

  const handleNavClick = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="help-page">
      {/* Sidebar navigation */}
      <aside className="help-page__sidebar">
        <div className="help-page__sidebar-header">
          <h2>User Guide</h2>
        </div>
        <nav className="help-page__nav">
          {sections.map(s => (
            <button
              key={s.id}
              className={`help-page__nav-item ${activeSection === s.id ? 'help-page__nav-item--active' : ''}`}
              onClick={() => handleNavClick(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="help-page__sidebar-footer">
          <button className="help-page__back-btn" onClick={() => history.goBack()}>
            ← Back
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="help-page__content">

        {/* ── Overview ─────────────────────────────────────────── */}
        <section id="overview" className="help-page__section">
          <h1 className="help-page__title">LiftOff — Product Launch Service</h1>
          <p className="help-page__lead">
            LiftOff is a product readiness and intake management system for tracking product
            launch activities across SKI releases. It provides structured intake forms,
            product hierarchy management, and reporting tools to coordinate work across teams.
          </p>

          <div className="help-page__card-grid">
            <div className="help-page__card">
              <div className="help-page__card-icon help-page__card-icon--blue">
                <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <h3>Dashboard</h3>
              <p>View and manage all product requests grouped by product.</p>
            </div>
            <div className="help-page__card">
              <div className="help-page__card-icon help-page__card-icon--green">
                <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3>Intake Requests</h3>
              <p>Submit CuC, CIC, BC, GPR, PPP, Privacy, and other request types via guided forms.</p>
            </div>
            <div className="help-page__card">
              <div className="help-page__card-icon help-page__card-icon--purple">
                <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                </svg>
              </div>
              <h3>Product Hierarchy</h3>
              <p>Link Packages, Commercial, and Technical products together.</p>
            </div>
            <div className="help-page__card">
              <div className="help-page__card-icon help-page__card-icon--orange">
                <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3>Reporting</h3>
              <p>Visualise data by SKI Release, Product, Release Type, or Product Hierarchy. Export to CSV.</p>
            </div>
          </div>
        </section>

        {/* ── Dashboard ────────────────────────────────────────── */}
        <section id="dashboard" className="help-page__section">
          <h2>Dashboard</h2>
          <p>
            The Dashboard is your main workspace. It displays all intake requests grouped by
            product, allowing you to see the status of work across your product portfolio at a glance.
          </p>

          <h3>Product Rows</h3>
          <p>
            Every product in the catalog is listed here, including products that have no requests yet.
            Click the expand arrow to see the requests underneath it. The following icons appear on each product row:
          </p>
          <table className="help-page__table">
            <thead>
              <tr><th>Icon</th><th>Action</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="help-page__icon-label">👁 View</span></td>
                <td>Opens the full Product Detail page showing all product fields and linked products.</td>
              </tr>
              <tr>
                <td><span className="help-page__icon-label">🔗 Link</span></td>
                <td>Opens the Product Link Summary — a quick view of the product's hierarchy links with an "Edit in Hierarchy" shortcut.</td>
              </tr>
              <tr>
                <td><span className="help-page__icon-label">✏️ Edit</span></td>
                <td>Opens the inline product editor (admin only).</td>
              </tr>
            </tbody>
          </table>

          <h3>Actions</h3>
          <ul className="help-page__list">
            <li><strong>New Product</strong> — Create a new product record (admin only).</li>
            <li><strong>New Request</strong> — Start a new intake request linked to a product.</li>
            <li><strong>Download CSV</strong> — Export request or product data to CSV.</li>
            <li><strong>Import CSV</strong> — Bulk import requests from a CSV file.</li>
            <li><strong>Expand All / Collapse All</strong> — Show or hide all request rows at once.</li>
          </ul>

          <h3>Searching and Filtering</h3>
          <p>
            Use the search bar to find requests by product name, request type code (e.g. <code>CUC</code>, <code>GPR</code>, <code>PPP</code>, <code>SOR</code>), request number, owner, or SKI release. Additional filters (status, request type, etc.) are available via the filter controls above the table.
          </p>

          <h3>View Toggles</h3>
          <p>
            Two toggles above the table let you control which products are visible. Your last setting is remembered — navigating to another page and returning restores the same view.
          </p>
          <table className="help-page__table">
            <thead>
              <tr><th>Toggle</th><th>On</th><th>Off</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>My Products</strong></td>
                <td>Shows only products where <em>you</em> are the assigned owner. On first visit, this is enabled automatically if you own any products (non-admin/developer roles only).</td>
                <td>Shows all products regardless of ownership.</td>
              </tr>
              <tr>
                <td><strong>Show All Products</strong></td>
                <td>Shows every product in the catalog, including those with no active requests.</td>
                <td>Hides products that have no requests. Packages are always visible regardless of this setting.</td>
              </tr>
            </tbody>
          </table>
          <p>
            The two toggles compose: with <strong>My Products on</strong> and <strong>Show All Products off</strong>, you see only your owned products that also have at least one request.
          </p>

          <h3>Request Rows</h3>
          <p>
            Expanding a product shows its individual requests. Each request row displays the
            request type (e.g. CIC, GPR - New Product), owner, SKI release, status, and action
            buttons to view, edit, or delete the request.
          </p>

          <h3>Your Requests</h3>
          <p>
            The <strong>Your Requests</strong> panel at the top of the Dashboard shows requests
            relevant to you, split into two groups:
          </p>
          <table className="help-page__table">
            <thead>
              <tr><th>Group</th><th>What it shows</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Waiting for approval</strong></td>
                <td>Requests <em>you submitted</em> that are in Draft or have been verified (shown with a <strong>Completed</strong> badge). Covers all approval-flow types and New Product Requests.</td>
              </tr>
              <tr>
                <td><strong>Please approve</strong></td>
                <td>Draft requests submitted by <em>other users</em> that fall within your approval scope. Admins and Developers see all types. Users with specific approver types assigned by an admin see only those types.</td>
              </tr>
            </tbody>
          </table>
          <p>
            Status badges: <strong>Draft</strong> — in progress; <strong>Awaiting approval</strong> — submitted by someone else and pending your review; <strong>Completed</strong> — verified. Once verified, a request disappears from the approver's "Please approve" list and remains in the requestor's "Waiting for approval" list as Completed.
          </p>
          <p>
            A <strong>Draft only / All</strong> toggle in the panel header controls which items are visible. <strong>Draft only</strong> (the default) shows only active requests needing attention. Switch to <strong>All</strong> to include completed items. The "Please approve" section is always Draft only regardless of the toggle, since completed requests no longer need action.
          </p>
        </section>

        {/* ── Creating Requests ────────────────────────────────── */}
        <section id="requests" className="help-page__section">
          <h2>Creating Requests</h2>
          <p>
            Requests capture the information needed to track product readiness activities.
            Each request is linked to a product and a SKI release.
          </p>

          <h3>How to Create a Request</h3>
          <ol className="help-page__list help-page__list--ordered">
            <li>Click <strong>New Request</strong> on the Dashboard.</li>
            <li>Select the request category (e.g. Product Launch, Product Support).</li>
            <li>Select the specific <strong>Request Type</strong> (e.g. CuC, GPR GA, Breaking Change).</li>
            <li>If a product row was selected before clicking New Request, the product name is pre-filled on the first step.</li>
            <li>On product selection fields, use the <strong>All / Commercial / Technical / Package</strong> filter bar to narrow the product list by type before selecting.</li>
            <li>Work through the multi-step form, answering questions as required.</li>
            <li>Click <strong>Save as Draft</strong> to save progress, or <strong>Submit</strong> when complete.</li>
          </ol>

          <h3>Navigating the Wizard</h3>
          <p>
            At the top of every wizard screen is a <strong>progress indicator</strong> showing a
            progress bar and named step nodes. You can navigate directly to any step by clicking its node:
          </p>
          <ul className="help-page__list">
            <li><strong>Clicking backward</strong> (a step you have already been on) — navigates immediately with no validation required.</li>
            <li><strong>Clicking forward</strong> (a step ahead of the current one) — validates all required fields on every step between the current step and the target. If any are missing the wizard stops and highlights them.</li>
          </ul>
          <p>Nodes that have no visible questions for the current request type are not clickable. Steps with no visible questions are skipped automatically.</p>
          <p>The wizard scrolls to the top automatically when you move to a new step or when a validation error is shown.</p>

          <h3>Auto-Save</h3>
          <p>
            The form automatically saves your progress as a Draft every 30 seconds while you are working.
            You can safely navigate away and return to a draft request at any time.
          </p>

          <h3>Editing a Request</h3>
          <p>
            Click the <strong>Edit</strong> icon on any request row in the Dashboard, or the Edit
            button on the Request Detail page. You can edit requests that are in Draft or
            In Progress status. The <strong>product name cannot be changed</strong> when editing — it
            always reflects the current name of the linked product record.
          </p>

          <h3>Viewing a Request</h3>
          <p>
            Click the <strong>View</strong> icon to open the read-only Request Detail page. This
            shows all answers, metadata, product type, and (for CuC requests) field changes
            compared to the prior SKI release.
          </p>

          <h3>Tips</h3>
          <ul className="help-page__list">
            <li>If a field does not appear, it may be hidden by a conditional question earlier in the form.</li>
            <li>For <strong>GPR GA</strong> requests, if an EA request exists for the same product, the wizard offers to pre-fill matching fields from it — saving significant data entry.</li>
            <li>For <strong>CiC</strong> requests, if a verified Breaking Change exists for the same product and SKI release, the wizard offers to pre-fill all matching fields from it.</li>
            <li>A duplicate guard on step 1 blocks creating the same product + type + SKI release combination twice.</li>
          </ul>
        </section>

        {/* ── Request Types ────────────────────────────────────── */}
        <section id="request-types" className="help-page__section">
          <h2>Request Types</h2>
          <p>
            Requests are organised into two categories. Each type has its own multi-step form,
            workflow, and notification behaviour.
          </p>

          <h3>Product Launch</h3>
          <div className="help-page__type-grid">
            <div className="help-page__type-card">
              <h4>CuC — Capability Update Catalog</h4>
              <p>
                The primary intake form for product capability updates across a SKI release.
                Covers cadence, adoption requirements, testing, delivery mechanism, and effort
                estimates. Supports field change tracking against the prior SKI release.
                Workflow: <strong>Draft → Verified</strong>.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>GPR — Go-to-market Product Readiness</h4>
              <p>Used to track readiness for products entering or updating the market. The <strong>SKI Release field is required</strong> for all GPR subtypes. Subtypes:</p>
              <ul className="help-page__list">
                <li><strong>New EA Service</strong> — New Early Access services entering the market</li>
                <li><strong>New GA Service</strong> — New services reaching General Availability</li>
                <li><strong>Existing Service</strong> — Updates to an already-released service</li>
                <li><strong>Marketplace</strong> — Products listed through the Guidewire Marketplace</li>
                <li><strong>New Internal Service</strong> — New internal services not directly customer-facing</li>
                <li><strong>Runbook Changes</strong> — Operational runbook updates for a release</li>
              </ul>
            </div>
            <div className="help-page__type-card">
              <h4>PPP — Product Packaging &amp; Pricing</h4>
              <p>
                Captures product packaging and pricing information for a SKI release. On Save as
                Verified, a modal prompts you to notify PPP reviewers by email with an auto-attached
                CSV. Workflow: <strong>Draft → Verified</strong>. Duplicates not allowed per
                product + SKI release. See the <button className="help-page__inline-link" onClick={() => handleNavClick('ppp')}>PPP section</button> for full field details.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>Pricing &amp; Packaging</h4>
              <p>
                Captures commercial pricing, packaging, and programme association details for a
                product at a specific SKI release. No email notification is sent — the request
                is saved as Draft and then verified directly by the requestor or an admin.
                Workflow: <strong>Draft → Verified</strong>. Excluded from reporting until Verified.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>Privacy</h4>
              <p>
                GDPR and data-privacy compliance assessment for product releases. A 32-question
                form covering PII collection, data storage, data processing, and external vendors.
                Sections 2–5 are only shown when at least one of the three Basic Yes/No questions
                is answered Yes. On-demand email notification to the Privacy Process Owner.
                Workflow: <strong>Draft → Verified</strong>. See the <button className="help-page__inline-link" onClick={() => handleNavClick('privacy')}>Privacy section</button> for full details.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>SalesOps Request</h4>
              <p>
                Captures commercial and pricing information required by the SalesOps team for
                a product or capability entering the market. Covers release type, product type,
                pricing metrics, usage components, regional restrictions, and provisioning details.
                When creating a SalesOps request, if a <strong>GPR GA</strong> request already
                exists for the same product, a prompt will offer to pre-fill matching fields from
                that GA request. On first save, LiftOff prompts you to <strong>notify the SalesOps
                Process Owner</strong> by email (with CSV attachment). Workflow:{' '}
                <strong>Draft → Verified</strong>. See the{' '}
                <button className="help-page__inline-link" onClick={() => handleNavClick('salesops')}>SalesOps section</button> for full field details.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>New Product Request</h4>
              <p>
                Submit a request for a brand-new product to be added to the LiftOff catalog.
                Fill in product details (name, type, categorization, owner, etc.) and submit —
                Product Operations will review and either create the product or decline with feedback.
                Workflow: <strong>Draft → Completed</strong>. See the{' '}
                <button className="help-page__inline-link" onClick={() => handleNavClick('new-product-request')}>New Product Request section</button> for full details.
              </p>
            </div>
          </div>

          <h3>Product Support</h3>
          <div className="help-page__type-grid">
            <div className="help-page__type-card">
              <h4>Breaking Change</h4>
              <p>
                Tracks breaking changes that may impact customers or other products in a release.
                Workflow: <strong>Draft → Verified</strong>.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>CiC — Customer Impact Communication</h4>
              <p>
                Documents the customer-facing communication required for a breaking change.
                When creating a new CiC, if a verified Breaking Change already exists for the same
                product and SKI release, a prompt offers to pre-fill all matching fields from that
                BC — saving significant data entry. On Save as Draft, a modal prompts you to notify
                the CiC Process Owner by email with an auto-attached CSV.
                Workflow: <strong>Draft → Verified</strong>.
              </p>
            </div>
            <div className="help-page__type-card">
              <h4>Capability Update Catalog (CuC)</h4>
              <p>Also available under Product Support for teams managing capability updates outside of a product launch context.</p>
            </div>
          </div>
        </section>

        {/* ── Products ─────────────────────────────────────────── */}
        <section id="products" className="help-page__section">
          <h2>Products</h2>
          <p>
            Products are the core entities in LiftOff. Every request is linked to a product.
            There are three product types that form a hierarchy:
          </p>

          <table className="help-page__table">
            <thead>
              <tr><th>Type</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Package</strong></td>
                <td>Top-level product grouping (e.g. a product suite). Contains one or more Commercial products.</td>
              </tr>
              <tr>
                <td><strong>Commercial</strong></td>
                <td>The commercially available product (e.g. PolicyCenter). Links to Technical products below and Packages above.</td>
              </tr>
              <tr>
                <td><strong>Technical</strong></td>
                <td>The underlying technical component or module. Linked to one or more Commercial products.</td>
              </tr>
            </tbody>
          </table>

          <h3>Creating a Product</h3>
          <p>
            Click <strong>New Product</strong> on the Dashboard (admin only). The create form is
            divided into sections — which sections appear depends on the product type selected:
          </p>
          <ul className="help-page__list">
            <li><strong>Package</strong> — Basic Information only.</li>
            <li><strong>Commercial</strong> — Basic Information + Detail (commercial fields).</li>
            <li><strong>Technical</strong> — Basic Information + Detail (technical fields) + Provisioning.</li>
          </ul>
          <p>A section progress bar at the top of the form shows your position (e.g. Basic → Detail → Provisioning). Product Name and Product Type are required.</p>

          <h3>Product Detail Fields</h3>
          <p><strong>Commercial products</strong> have a Detail section with: Business Unit, Product Family, Based On, Availability Date, Installation, End Of Life Date, Discontinued Date, Core Product Type, and Predecessor Product.</p>
          <p><strong>Technical products</strong> have a Detail section with: Fulfilment Quantity Calculation Method, Polaris ID, Has Customer Update Adoption Criteria (GA release type only), PPP Apply, and Predecessor Product. They also have a <strong>Provisioning</strong> section: Provisioning Required, Provisioning Team, Automated, NPE/Prod, and Action on Renewal.</p>

          <h3>Viewing a Product</h3>
          <p>
            Click the <strong>View (eye) icon</strong> on a product row to open the Product Detail
            page. This page has two tabs:
          </p>
          <ul className="help-page__list">
            <li><strong>Details</strong> — read-only view of all product fields and linked products.</li>
            <li><strong>History</strong> — full audit trail of every field change: what changed, old and new values, who made the change, and when.</li>
          </ul>

          <h3>Activating / Deactivating a Product</h3>
          <p>
            From the Product Detail page, admins can toggle a product between Active and Inactive
            using the <strong>Deactivate</strong> / <strong>Reactivate</strong> button.
          </p>
        </section>

        {/* ── Product Hierarchy ────────────────────────────────── */}
        <section id="hierarchy" className="help-page__section">
          <h2>Product Hierarchy</h2>
          <p>
            The Product Hierarchy page provides a three-panel UI for managing the relationships
            between Packages, Commercial products, and Technical products.
          </p>

          <h3>Three-Panel Layout</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Panel</th><th>Shows</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>Left</strong></td><td>All Package products. Click one to load its linked Commercial products.</td></tr>
              <tr><td><strong>Middle</strong></td><td>All Commercial products. Highlights those linked to the selected Package. Click one to load its Technical products.</td></tr>
              <tr><td><strong>Right</strong></td><td>Technical products linked to the selected Commercial product. Toggle links on/off.</td></tr>
            </tbody>
          </table>

          <h3>Linking Products</h3>
          <ol className="help-page__list help-page__list--ordered">
            <li>Select a <strong>Package</strong> in the left panel.</li>
            <li>In the middle panel, click <strong>Link</strong> next to a Commercial product to associate it with the package.</li>
            <li>Select a <strong>Commercial</strong> product to load its Technical products in the right panel.</li>
            <li>Toggle Technical products on or off using the checkboxes, then click <strong>Save</strong>.</li>
          </ol>

          <h3>Navigating from the Dashboard</h3>
          <p>
            Clicking the <strong>🔗 Link icon</strong> on a product row opens the Link Summary modal.
            Clicking <strong>Edit in Hierarchy</strong> navigates directly to the Product Hierarchy
            page with the relevant product pre-selected based on type:
          </p>
          <ul className="help-page__list">
            <li><strong>Commercial</strong> — the commercial product is pre-selected in the middle panel.</li>
            <li><strong>Technical</strong> — the first linked commercial product is pre-selected.</li>
            <li><strong>Package</strong> — the package and its first commercial product are pre-selected.</li>
          </ul>
        </section>

        {/* ── Reporting ────────────────────────────────────────── */}
        <section id="reporting" className="help-page__section">
          <h2>Reporting</h2>
          <p>
            The Reporting page provides multiple views of your intake data with filtering,
            network graph visualisation, and CSV export.
          </p>

          <h3>View Modes</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>View</th><th>Structure</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>By SKI Release</strong></td><td>SKI Release → Request Type → Product</td></tr>
              <tr><td><strong>By Product</strong></td><td>Product → SKI Release → Request Type</td></tr>
              <tr><td><strong>By Release Type</strong></td><td>SKI Release → EA / RA / GA → Request Type → Product</td></tr>
              <tr><td><strong>By Hierarchy</strong></td><td>Package → Commercial Product → Technical Product → SKI Release → Request Type. Shows the full linked product hierarchy overlaid with request data. Technical products with no requests are still shown (count 0). Unlinked commercial products appear under an <em>Unpackaged</em> group.</td></tr>
              <tr><td><strong>Grid View</strong></td><td>Tabular format — supports column selection and CSV download</td></tr>
            </tbody>
          </table>

          <h3>Network Graph</h3>
          <p>
            In the first three view modes, data is displayed as an interactive network graph.
            Click any node to expand or collapse it. Each node type is colour-coded — refer to
            the legend below the graph.
          </p>

          <h3>Filtering</h3>
          <p>
            Use the filter bar to narrow results by Request Type, Status, Owner, Product, SKI
            Release, GPR Subtype, Created By, Location, Work Areas, and Categorization. Click
            <strong> Apply Filters</strong> to refresh the data or <strong>Clear</strong> to reset.
            Use <strong>Manage Filters</strong> to toggle which filters are visible.
          </p>

          <h3>Summary Statistics</h3>
          <p>
            The statistics bar above the graph shows live counts of SKI Releases, Products, and
            Requests matching the current filters.
          </p>
        </section>

        {/* ── Exporting Data ───────────────────────────────────── */}
        <section id="export" className="help-page__section">
          <h2>Exporting Data</h2>

          <h3>Download CSV (Dashboard)</h3>
          <p>
            Click <strong>Download CSV</strong> on the Dashboard to open the export modal.
            Select a Request Type and (where applicable) a SKI Release, then click
            <strong> Download CSV</strong>.
          </p>

          <table className="help-page__table">
            <thead>
              <tr><th>Request Type</th><th>Exports</th><th>Filename</th></tr>
            </thead>
            <tbody>
              <tr><td>All Request Types</td><td>All requests with all fields</td><td>CPLS_All_All_DateTime.csv</td></tr>
              <tr><td>Product Catalog</td><td>Products with qualifying requests for the selected SKI release (see below)</td><td>CPLS_ProductCatalog_SKI_Date.csv</td></tr>
              <tr><td>CuC</td><td>CuC requests for the selected SKI release</td><td>CPLS_SKI_CuC_DateTime.csv</td></tr>
              <tr><td>Pricing &amp; Packaging</td><td>P&amp;P requests for the selected SKI release</td><td>PP_SKI_Date.csv</td></tr>
              <tr><td>Other types</td><td>Requests matching type and SKI release</td><td>CPLS_SKI_Type_DateTime.csv</td></tr>
            </tbody>
          </table>

          <h3>Product Catalog Export</h3>
          <p>
            When <strong>Product Catalog</strong> is selected and a SKI release is chosen, only
            products with at least one qualifying request for that release are included. Qualifying
            request types are: <strong>GPR EA</strong>, <strong>GPR GA</strong>, <strong>GPR
            Marketplace</strong>, and <strong>Pricing &amp; Packaging</strong>. Products with only
            other request types (e.g. Breaking Change, CuC, SalesOps) are excluded.
          </p>
          <p>
            Each row includes full product detail — Basic fields, Commercial Detail, Technical
            Detail, Provisioning, and Pricing &amp; Packaging answers from the most recent P&amp;P
            request for that product and release.
          </p>

          <h3>Previewing Product Catalog Records</h3>
          <p>
            When Product Catalog is selected and there are matching records, a green <strong>Preview
            Records</strong> button appears in the modal footer. Clicking it opens a record-by-record
            viewer:
          </p>
          <ul className="help-page__list">
            <li>Use <strong>‹</strong> and <strong>›</strong> to step through records — a counter shows your position (e.g. "Record 3 of 6")</li>
            <li>Fields are grouped into sections: Basic, Commercial Detail, Technical Detail, Provisioning, and Pricing &amp; Packaging</li>
            <li>Only populated fields are shown; empty sections display a dash</li>
            <li><strong>Download CSV</strong> is available directly from the preview — no second API call is made</li>
            <li><strong>← Back</strong> returns to the filter step</li>
          </ul>

          <h3>Download Products CSV (Reporting)</h3>
          <p>
            In the <strong>By SKI Release</strong> reporting view, filter by a specific SKI release
            to reveal the <strong>Download Products (CSV)</strong> button. This exports a
            product-level CSV for that release.
          </p>

          <h3>Grid View CSV (Reporting)</h3>
          <p>
            Switch to <strong>Grid View</strong> in Reporting and click <strong>Download CSV</strong>
            to export the current filtered grid data. Use <strong>Manage Columns</strong> to control
            which columns are included.
          </p>
        </section>

        {/* ── Admin Features ───────────────────────────────────── */}
        <section id="admin" className="help-page__section">
          <h2>Admin Features</h2>
          <p>The application uses role-based access control mapped from OKTA AD groups. The four roles are:</p>
          <table className="help-page__table">
            <thead>
              <tr>
                <th>Role</th>
                <th>AD Group</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Admin</strong></td>
                <td><code>pdo-liftoff-admins</code></td>
                <td>Full access including Admin page and Product Hierarchy</td>
              </tr>
              <tr>
                <td><strong>Developer</strong></td>
                <td><code>pdo-liftoff-developer</code></td>
                <td>Full access including Admin page and Product Hierarchy</td>
              </tr>
              <tr>
                <td><strong>Approver</strong></td>
                <td><code>pdo-liftoff-approvers</code></td>
                <td>All features except Admin page and Product Hierarchy. Can approve CiC and BC requests.</td>
              </tr>
              <tr>
                <td><strong>User</strong></td>
                <td><code>pdo-liftoff</code></td>
                <td>All features except Admin page and Product Hierarchy.</td>
              </tr>
            </tbody>
          </table>

          <h3>Admin Dashboard</h3>
          <p>Accessible from the navigation (admin and developer roles only). Provides:</p>
          <ul className="help-page__list">
            <li><strong>Statistics</strong> — Total counts for products, SKI releases, request types, and requests grouped by release.</li>
            <li><strong>Pending Product Requests</strong> — Review and action <em>New Product Requests</em> submitted by users. For each pending request, admins can <strong>View</strong> the full submission, <strong>Create Product</strong> (approve and add to catalog), or <strong>Decline</strong> (reject with a reason). See the <button className="help-page__inline-link" onClick={() => handleNavClick('new-product-request')}>New Product Request section</button> for the full workflow.</li>
            <li><strong>Seed Data Management</strong> — Two one-time import actions: <em>Import Products</em> (loads the product catalog from CSV) and <em>Load CUC Seed Data</em> (links products to SKI releases). Both are required after a fresh database setup.</li>
            <li><strong>SKI Release Management</strong> — Create and edit SKI releases with 9 milestone dates (PA, GA, CUC Verified, Breaking Change, CiC, SalesOps, Privacy, GTM/GPRs, PPP). Copy Verified CuC requests between releases.</li>
            <li><strong>User Roles</strong> — Assign system roles to users. The scrollable table shows every user and their current role. Click <strong>Edit</strong> on any row (or select a user from the dropdown) then choose a role and click Save. Role changes take effect on the user's next page load. Admins cannot change their own role. Available roles: Admin, Developer, Approver, Requestor. Note: until the Okta groups claim is configured, new users default to Requestor on first login and must be promoted here.</li>
            <li><strong>Approver Settings</strong> — Assign request-type approval responsibilities to individual users. Select a user, check the types they should approve (Breaking Change, CiC, PPP, Privacy, SalesOps), and click Save. Users with types assigned will see matching Draft requests in their "Please approve" list on the Dashboard. Admins and Developers always see all types regardless of this setting.</li>
            <li><strong>System Settings</strong> — Toggle email notifications on or off at runtime. The toggle controls whether the system sends emails for any request type. Changes take effect immediately without a server restart.</li>
          </ul>

          <h3>Product Management</h3>
          <ul className="help-page__list">
            <li>Create new products via the <strong>New Product</strong> button on the Dashboard.</li>
            <li>Edit products via the <strong>Edit (pencil) icon</strong> on product rows.</li>
            <li>Activate or deactivate products from the Product Detail page.</li>
          </ul>

          <h3>Request Management</h3>
          <ul className="help-page__list">
            <li>Delete any request (not just your own) using the Delete icon.</li>
            <li>Change the status of any request.</li>
          </ul>
        </section>

        {/* ── Status Workflow ──────────────────────────────────── */}
        <section id="status" className="help-page__section">
          <h2>Status Workflow</h2>
          <p>Requests move through the following statuses:</p>

          <div className="help-page__status-flow">
            {[
              { status: 'Draft',    color: 'gray',   desc: 'Saved but not yet submitted. Can be edited freely.' },
              { status: 'Verified', color: 'purple', desc: 'Submitted and fully verified. Eligible for copying to the next release.' },
            ].map((s, i, arr) => (
              <React.Fragment key={s.status}>
                <div className="help-page__status-step">
                  <div className={`help-page__status-badge help-page__status-badge--${s.color}`}>
                    {s.status}
                  </div>
                  <p>{s.desc}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="help-page__status-arrow">→</div>
                )}
              </React.Fragment>
            ))}
          </div>

          <h3>Status Display for New Product Requests</h3>
          <p>
            New Product Requests use the same <strong>Draft → Verified</strong> internal status, but the
            dashboard and admin panel display <strong>Completed</strong> instead of Verified to better
            reflect that the product has been created and the request is closed. The underlying status
            value remains <em>Verified</em>.
          </p>
        </section>

        {/* ── Capability Update Catalog ────────────────────────── */}
        <section id="cuc" className="help-page__section">
          <h2>Capability Update Catalog (CuC)</h2>

          <h3>Executive Summary</h3>
          <p>
            Guidewire Cloud Infrastructure (GCI) updates every 2 weeks, while other Guidewire
            Solutions update every ski resort release (core products), or at other cadences such
            as monthly.
          </p>
          <p>
            To establish a consolidated view of Guidewire solutions and capabilities with update
            adoption requirements, we catalog all key customer-facing (commercial) capabilities
            with their respective update details. This information provides TAMs, Customer Success,
            and other key customer-facing groups within Guidewire a more holistic picture of what
            needs to be considered for updates during each ski release launch cycle.
          </p>
          <p>Example uses of the catalog:</p>
          <ul className="help-page__list">
            <li>Items that impact the customer side will inform account-facing teams for update planning with customers.</li>
            <li>Breaking changes, which require customers to do additional work to absorb the updates.</li>
            <li>Products that are part of the Update Preview process.</li>
          </ul>

          <h3>Actions for Capability Owners</h3>
          <p><strong>Owners of existing commercial capabilities</strong> must verify their capabilities each ski release:</p>
          <ol className="help-page__list help-page__list--ordered">
            <li>Select the capability, then edit the Capability Update Catalog request type for the current ski release from the Dashboard. If the capability doesn't exist, see below for new commercial capabilities.</li>
            <li>Validate the update adoption criteria.</li>
            <li>Validate the associated owners.</li>
            <li>Review the tooltips for each field (see below).</li>
            <li>Submit the request to set the status to <strong>Verified</strong>, or save as <strong>Draft</strong> if not yet ready.</li>
          </ol>
          <p><strong>Owners of new commercial capabilities</strong> should:</p>
          <ol className="help-page__list help-page__list--ordered">
            <li>Click <strong>+ New Product</strong> and create the new capability.</li>
            <li>Click <strong>+ New Request</strong>, select the new capability, ski release, and other fields, then submit to verify.</li>
          </ol>

          <h3>Field Definitions (Tooltip Values)</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Field</th><th>Description</th></tr>
            </thead>
            <tbody>
              {[
                ['Product Name', 'Name of GA or RA solution/capability that is involved with a customer update.'],
                ['Owner', 'Assigned steward to the row data, likely a PM.'],
                ['Ski Release', 'The ski release launch cycle for which this capability is to be verified.'],
                ['Cadence of Producing an Update', 'How often is a release produced?'],
                ['Update Adoption Required', 'Is the produced release required to adopt?'],
                ['Update Owner', 'Who does the update?'],
                ['Tenancy (Side) Updated', 'What side is updated?'],
                ['Layer Definition Used', 'Are layers defined?'],
                ['Language Resources Available', 'Does the App have language resources provided by CLOC (continuous localization)? If the customer needs a language other than the standard 10 languages provided, they must request language support. Note: if an upgrade replaces translations in a newer release, the customer may lose any translation updates they have made.'],
                ['Product Version', 'Is there a new version of the product?'],
                ['Update Testing Mechanism', 'How is the update being tested to ensure no breaks are delivered to customers?'],
                ['Update Qualification Benchmark', 'Is there a qualification level the testing must achieve to be considered releasable to customers?'],
                ['Update Delivery Mechanism', 'What is the delivery mechanism to deliver the update?'],
                ['Update Request Mechanism', 'Link to the formal way to request this update (e.g. Sno Ticket, Self-service through IG UI).'],
                ['Update Process', 'Link to the documented process for the update (including division of labor).'],
                ['Lead Time Required (Weeks)', 'Does the update require lead time?'],
                ['Guidewire Update Work (Weeks)', 'How long does it take GW to deliver the update?'],
                ['Additional Customer Effort with Update (Weeks)', 'How much additional effort is required from the customer?'],
                ['Customer Validation Work (Weeks)', 'How long do we expect it will take the customer to validate the update?'],
                ['Total Work Time (Weeks)', 'Total time the customer is involved in the update.'],
                ['Total Time (Weeks)', 'Total time end-to-end.'],
                ['Reason', 'What is the reason for more effort for customers?'],
                ['Release Update Breaking Change', 'Will the update to the new capability version, including associated dependencies such as common libraries, require the customer to do manual work to implement the capability?'],
                ['Included in an Update Preview (as of the PA Date)', 'At the PA date, an EA program for an Update Preview is added — will this service or capability be available?'],
              ].map(([field, desc]) => (
                <tr key={field}>
                  <td><strong>{field}</strong></td>
                  <td>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── PPP ─────────────────────────────────────────────── */}
        <section id="ppp" className="help-page__section">
          <h2>Product Packaging &amp; Pricing (PPP)</h2>

          <h3>Overview</h3>
          <p>
            The PPP request type captures product packaging and pricing information for a SKI
            release. It uses a Draft → Verified workflow. When you first save as Draft, LiftOff
            prompts you to notify the PPP reviewers by email; the notification includes an
            auto-generated CSV of all answers. You can re-send at any time via the banner link.
          </p>
          <ul className="help-page__list">
            <li><strong>Workflow:</strong> Draft → Verified</li>
            <li><strong>Notification:</strong> On-demand — prompted when you first save as Draft</li>
            <li><strong>Duplicates:</strong> Not allowed — one PPP per product + SKI release</li>
            <li><strong>Reporting:</strong> Excluded until status = Verified</li>
          </ul>

          <h3>Form Sections &amp; Fields</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Section</th><th>Field</th><th>Required</th></tr>
            </thead>
            <tbody>
              <tr><td rowSpan={3}><strong>Basic Information</strong></td><td>Product Name</td><td>Yes</td></tr>
              <tr><td>SKI Release</td><td>Yes</td></tr>
              <tr><td>Owner</td><td>Yes</td></tr>
              <tr><td rowSpan={8}><strong>Product Packaging &amp; Pricing</strong></td><td>New/Existing Product</td><td>Yes</td></tr>
              <tr><td>Product Type</td><td>Yes</td></tr>
              <tr><td>Pricing Model</td><td>Yes</td></tr>
              <tr><td>Packaging Tier</td><td>No</td></tr>
              <tr><td>List Price</td><td>No</td></tr>
              <tr><td>Discount Structure</td><td>No</td></tr>
              <tr><td>Bundling Options</td><td>No</td></tr>
              <tr><td>Notes / Justification</td><td>No</td></tr>
            </tbody>
          </table>
        </section>

        {/* ── Privacy ──────────────────────────────────────────── */}
        <section id="privacy" className="help-page__section">
          <h2>Privacy</h2>

          <h3>Overview</h3>
          <p>
            The Privacy request type is a GDPR and data-privacy compliance assessment that must
            be completed when a product release involves changes to how personal data is collected,
            stored, processed, or shared. It consists of 32 questions across 5 sections.
          </p>
          <ul className="help-page__list">
            <li><strong>Workflow:</strong> Draft → Verified</li>
            <li><strong>Notification:</strong> On-demand — triggered when you save as Draft; notifies the Privacy Process Owner by email with an auto-generated CSV</li>
            <li><strong>Duplicates:</strong> Allowed — multiple Privacy requests per product + SKI release are permitted</li>
            <li><strong>Reporting:</strong> Excluded until status = Verified</li>
          </ul>

          <h3>Conditional Section Gating</h3>
          <p>
            Section 1 (Basic Information) is always shown. Sections 2–5 are only visible when
            at least one of the three Yes/No trigger questions in Section 1 is answered <strong>Yes</strong>:
          </p>
          <ul className="help-page__list">
            <li>Are you creating/re-designing functionality that changes how PII is collected?</li>
            <li>Are you allowing access to/from a new internal or external data source?</li>
            <li>Are you adding a new architecture change to permit sharing or use of Customer Data?</li>
          </ul>
          <p>
            If all three are answered <strong>No</strong>, only Section 1 is required and the form
            proceeds directly to Save — no further questions need to be answered.
          </p>

          <h3>Form Sections</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Section</th><th>Questions</th><th>Visibility</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>1. Basic Information</strong></td>
                <td>Product Name, SKI Release, Owner, Master Feature Link, and the 3 Yes/No trigger questions</td>
                <td>Always shown — all 7 fields are mandatory</td>
              </tr>
              <tr>
                <td><strong>2. PII Details</strong></td>
                <td>New PII types, purpose, environments, confirmation PII not in logs</td>
                <td>Shown only when any Section 1 trigger = Yes</td>
              </tr>
              <tr>
                <td><strong>3. Data Storage</strong></td>
                <td>Regional data residency, right to erasure, customer-controlled retention, machine-readable export</td>
                <td>Shown only when any Section 1 trigger = Yes</td>
              </tr>
              <tr>
                <td><strong>4. Data Processing</strong></td>
                <td>Automated decision-making, privacy by design, pseudonymisation, do-not-process flag</td>
                <td>Shown only when any Section 1 trigger = Yes</td>
              </tr>
              <tr>
                <td><strong>5. External Vendors</strong></td>
                <td>External vendor access to personal data, third-party data inputs</td>
                <td>Shown only when any Section 1 trigger = Yes</td>
              </tr>
            </tbody>
          </table>

          <h3>GDPR Notes</h3>
          <p>
            Many fields in sections 3–5 contain detailed GDPR/legal explanatory notes. These are
            shown as a collapsible <strong>"Learn more ▼"</strong> toggle to keep the form readable.
            Click the toggle to expand the full compliance context for that field.
          </p>
          <p>
            Fields with explain sub-questions (e.g. "please explain why…") appear automatically
            when the parent radio answer triggers them — for example, answering <strong>No</strong> to
            "Can PII be erased?" reveals a text field asking for the explanation.
          </p>
        </section>

        {/* ── SalesOps Request ─────────────────────────────────── */}
        <section id="salesops" className="help-page__section">
          <h2>SalesOps Request</h2>
          <p>
            The SalesOps request type captures the commercial and pricing information required
            by the SalesOps team before a product or capability can be sold. It is typically
            raised alongside a GPR GA request for any product reaching General Availability
            with a commercial offering.
          </p>

          <h3>Email Notification</h3>
          <p>
            When you save a SalesOps request for the first time, LiftOff prompts you to{' '}
            <strong>notify the SalesOps Process Owner</strong>:
          </p>
          <ul className="help-page__list">
            <li>
              <strong>Notify Process Owner</strong> — saves the request and sends an email to the
              SalesOps team group. The email includes a standard summary (Request Number, Product,
              SKI Release, Owner, Submitted By, Date Created), key request details (Release Type,
              Product Type, description or changes, regional restrictions, dependencies), and a
              CSV export of the full request as an attachment.
            </li>
            <li>
              <strong>Just Save</strong> — saves the request without sending a notification.
            </li>
          </ul>
          <p>
            Once a notification has been sent, a banner appears in the form showing the date it was
            sent. Use the <strong>"Re-send notification"</strong> link in the banner to send again
            at any time (for example, after making significant edits).
          </p>

          <h3>GA Request Pre-fill</h3>
          <p>
            When creating a new SalesOps request, as soon as you select a <strong>Product or
            Capability Name</strong>, the system checks whether a <strong>GPR GA</strong> request
            already exists for that product. If one is found, a prompt appears offering to
            pre-fill the following fields from the GA request:
          </p>
          <ul className="help-page__list">
            <li><strong>SKI Release</strong> — carried over from the GA request</li>
            <li><strong>Description</strong> — populated from the GA purpose field</li>
            <li><strong>Regional restrictions</strong> — populated from the GA regions field</li>
            <li><strong>Provisioning method</strong> — populated from the GA provisioning KT link</li>
            <li><strong>Customer access communication</strong> — populated from the GA comms plan link</li>
          </ul>
          <p>
            You can choose <strong>Yes, pre-fill fields</strong> to apply these values, or{' '}
            <strong>No, start blank</strong> to fill the form from scratch. Pre-filled values
            can be edited at any point before submission.
          </p>

          <h3>Fields</h3>
          <h4>Basic</h4>
          <table className="help-page__table">
            <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>Product Name</td><td>Dropdown</td><td>Required. Triggers GA pre-fill check on selection.</td></tr>
              <tr><td>SKI Release</td><td>Dropdown</td><td>Required. Target SKI release for this request.</td></tr>
              <tr><td>Owner</td><td>Email</td><td>Required. Email address of the responsible owner or PM.</td></tr>
              <tr><td>Is this being released as GA, RA, or EA?</td><td>Dropdown</td><td>Required.</td></tr>
              <tr><td>Is this a new commercial product or a change to an existing product?</td><td>Dropdown</td><td>Required. Controls which subsequent sections are shown.</td></tr>
              <tr><td>One sentence description of your offering</td><td>Text</td><td>Shown for New Commercial Product only.</td></tr>
              <tr><td>Which existing product(s) is changing and how?</td><td>Textarea</td><td>Shown for Change to Existing Product only.</td></tr>
              <tr><td>Commercial or technical dependencies</td><td>Textarea</td><td>Optional.</td></tr>
              <tr><td>Regional restrictions</td><td>Textarea</td><td>Optional. e.g. not available in France due to GDPR.</td></tr>
              <tr><td>Eligibility limitations</td><td>Textarea</td><td>Optional. e.g. only available to Workers Comp carriers.</td></tr>
            </tbody>
          </table>

          <h4>New Commercial Products</h4>
          <p>Shown only when product type is <em>New Commercial Product</em>.</p>
          <table className="help-page__table">
            <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>Pricing metric</td><td>Dropdown</td><td>Required. Variable DWP based / Variable Unit/Quantity / Other.</td></tr>
              <tr><td>Pricing metric details</td><td>Textarea</td><td>Required. Formula, unit, and packaging details.</td></tr>
              <tr><td>Discountable</td><td>Textarea</td><td>Optional. Whether discretionary discounting applies.</td></tr>
              <tr><td>Purchase per xCenter</td><td>Textarea</td><td>Optional. Whether separate purchase is needed per xCenter.</td></tr>
              <tr><td>Purchase per Star</td><td>Textarea</td><td>Optional. Whether separate purchase is needed per Star.</td></tr>
            </tbody>
          </table>

          <h4>Changes to Existing Products</h4>
          <p>Shown only when product type is <em>Change to Existing Product</em>.</p>
          <table className="help-page__table">
            <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>Pricing/packaging impact</td><td>Textarea</td><td>Optional. Whether the change affects existing pricing.</td></tr>
              <tr><td>Existing customer cost</td><td>Textarea</td><td>Optional. Whether existing customers get the change at no extra cost.</td></tr>
            </tbody>
          </table>

          <h4>Usage Component</h4>
          <table className="help-page__table">
            <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>New or changed usage components</td><td>Textarea</td><td>Optional. Usage-based fees or PPP limits.</td></tr>
              <tr><td>Overage payment method</td><td>Dropdown</td><td>Additional fees ($$$) or Guidewire Credits. Shown when usage components are entered.</td></tr>
              <tr><td>Overage pricing (fees)</td><td>Textarea</td><td>Shown when overage method is Additional fees.</td></tr>
              <tr><td>Overage pricing (GCs)</td><td>Textarea</td><td>Shown when overage method is Guidewire Credits.</td></tr>
              <tr><td>Usage tracking &amp; reporting</td><td>Textarea</td><td>Optional. Confirm reporting mechanisms are in place.</td></tr>
              <tr><td>NPE considerations</td><td>Textarea</td><td>Optional. Non-production environment pricing or requirements.</td></tr>
              <tr><td>Provisioning method</td><td>URL</td><td>Optional. Link to provisioning documentation (Day 1 vs. customer-request delivery).</td></tr>
              <tr><td>Customer access communication</td><td>URL</td><td>Optional. Link to documentation describing how customers gain access.</td></tr>
            </tbody>
          </table>
        </section>

        {/* ── New Product Request ──────────────────────────────── */}
        <section id="new-product-request" className="help-page__section">
          <h2>New Product Request</h2>
          <p>
            The <strong>New Product Request (NPR)</strong> is used when you need to add a brand-new
            product to the LiftOff catalog. Unlike other request types, the product does not exist yet —
            you are asking Product Operations to create it. Once approved, the product becomes available
            across the system for use in other request types.
          </p>

          <h3>When to Use</h3>
          <ul className="help-page__list">
            <li>You are launching a product that is not yet in the LiftOff catalog.</li>
            <li>You need a product record created before you can raise other requests (GPR, CuC, etc.) against it.</li>
            <li>A product has been renamed, split, or spun off and requires a new entry.</li>
          </ul>

          <h3>How to Submit a Request</h3>
          <ol className="help-page__list">
            <li>On the Dashboard, click <strong>New Request</strong> and select <strong>New Product Request</strong> from the Product Launch section.</li>
            <li>Complete the multi-step form. All required fields must be filled before you can submit.</li>
            <li>Click <strong>Submit Request</strong>. A confirmation modal will appear — review the details and confirm.</li>
            <li>Product Operations is automatically notified by email.</li>
            <li>Your request appears in the <strong>Your Requests</strong> panel on your Dashboard with status <strong>Draft</strong> (pending review).</li>
          </ol>

          <h3>Form Fields</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Field</th><th>Section</th><th>Required</th><th>Notes</th></tr>
            </thead>
            <tbody>
              <tr><td>Product Name</td><td>Product Details</td><td>Yes</td><td>Must be unique — the form checks the catalog as you type.</td></tr>
              <tr><td>Product Type</td><td>Product Details</td><td>Yes</td><td>Commercial or Technical.</td></tr>
              <tr><td>Release Type</td><td>Product Details</td><td>Yes</td><td>e.g. GA, EA, RA.</td></tr>
              <tr><td>Product Code</td><td>Product Details</td><td>Yes</td><td>Short identifier used in the catalog.</td></tr>
              <tr><td>Description</td><td>Product Details</td><td>Yes</td><td>Brief description of the product.</td></tr>
              <tr><td>Categorization</td><td>Product Details</td><td>Yes</td><td>e.g. Cloud-Native Service, Hybrid.</td></tr>
              <tr><td>Product Owner</td><td>Product Details</td><td>Yes</td><td>Select from existing users.</td></tr>
              <tr><td>Predecessor Product</td><td>Product Details</td><td>No</td><td>Any product this replaces. Shown for both Commercial and Technical.</td></tr>
              <tr><td>Business Unit</td><td>Commercial Details</td><td>Yes</td><td>Cascades to filter Performance Matrix Group options.</td></tr>
              <tr><td>Performance Matrix Group</td><td>Commercial Details</td><td>Yes</td><td>Filtered by Business Unit.</td></tr>
              <tr><td>Product Family</td><td>Commercial Details</td><td>Yes</td><td>Filtered by Performance Matrix Group.</td></tr>
              <tr><td>Core Product Type</td><td>Commercial Details</td><td>No</td><td>Dropdown: Core Cloud, Core Term. Only populate for core products (PC, BC, CC).</td></tr>
              <tr><td>Has Customer Update Adoption Criteria?</td><td>Technical Details</td><td>No (GA only)</td><td>Select Yes only for a key technical product or capability requiring update planning with customers. Disabled and forced to No for non-GA release types. Automatically corrected to No on save if release type is not GA.</td></tr>
              <tr><td>Based On</td><td>Commercial Details</td><td>No</td><td>Parent or source product if derived.</td></tr>
              <tr><td>PPP Apply?</td><td>Technical Details</td><td>Yes</td><td>Whether Pricing &amp; Packaging applies to this technical product.</td></tr>
              <tr><td>Provisioning Required?</td><td>Technical Details</td><td>Yes</td><td>Whether this product requires infrastructure provisioning.</td></tr>
              <tr><td>Provisioning Team</td><td>Technical Details</td><td>Yes (if Provisioning=Yes)</td><td>Dropdown of 15 team options.</td></tr>
              <tr><td>Provisioning Automated?</td><td>Technical Details</td><td>Yes (if Provisioning=Yes)</td><td>Yes / No.</td></tr>
              <tr><td>Provisioned in NPE/Prod</td><td>Technical Details</td><td>Yes (if Provisioning=Yes)</td><td>Dropdown: NPE, Prod, NPE + Prod.</td></tr>
              <tr><td>Has Provisioning Action on Renewal?</td><td>Technical Details</td><td>Yes (if Provisioning=Yes)</td><td>Yes / No.</td></tr>
              <tr><td>Fulfilment Quantity Calculation Method</td><td>Technical Details</td><td>No</td><td>Dropdown: Always One or Aggregate.</td></tr>
              <tr><td>Polaris ID</td><td>Technical Details</td><td>No</td><td>Polaris system identifier for this technical product.</td></tr>
              <tr><td>Business Justification</td><td>Business Case</td><td>Yes</td><td>Why this product needs to be added to the product catalog.</td></tr>
            </tbody>
          </table>

          <h3>Status Workflow</h3>
          <div className="help-page__status-flow">
            {[
              { status: 'Draft',     color: 'gray',   desc: 'Submitted and awaiting Product Operations review.' },
              { status: 'Completed', color: 'purple', desc: 'Product Operations approved the request and created the product. A link to the new product record appears on the request detail page.' },
            ].map((s, i, arr) => (
              <React.Fragment key={s.status}>
                <div className="help-page__status-step">
                  <div className={`help-page__status-badge help-page__status-badge--${s.color}`}>
                    {s.status}
                  </div>
                  <p>{s.desc}</p>
                </div>
                {i < arr.length - 1 && <div className="help-page__status-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
          <p>
            If your request is <strong>declined</strong>, it returns to <strong>Draft</strong> with a
            reason displayed on the request. You can update the details and resubmit.
          </p>

          <h3>Tracking Your Request</h3>
          <p>
            Submitted requests appear in the <strong>Your Requests</strong> panel at the top
            of your Dashboard. The panel shows the product name, your name as requestor, and the current
            status. Each row has icon action buttons:
          </p>
          <ul className="help-page__list">
            <li><strong>Eye icon</strong> — View the full request detail.</li>
            <li><strong>Pencil icon</strong> — Edit the request (Draft only). Shows as <em>Edit &amp; Resubmit</em> tooltip if the request was declined.</li>
            <li><strong>Trash icon</strong> — Delete the request (Draft only). This is permanent and cannot be undone.</li>
            <li><strong>Arrow icon</strong> — View the created product in the catalog (Completed requests only).</li>
          </ul>

          <h3>After Approval</h3>
          <p>
            Once Product Operations creates the product, you will receive a confirmation email.
            The request status changes to <strong>Completed</strong> and a <strong>View Product →</strong>
            link appears on the request detail page, taking you directly to the new product record in
            the catalog. You can then raise GPR, CuC, PPP, and other requests against the new product.
          </p>

          <h3>For Admins — Reviewing Product Requests</h3>
          <p>
            Pending product requests are shown in the <strong>Pending Product Requests</strong> table on
            the Admin page. For each request:
          </p>
          <ul className="help-page__list">
            <li><strong>View</strong> — Opens the full request detail in a new tab.</li>
            <li><strong>Create Product</strong> — Opens a modal pre-filled with the submitted details. Review and adjust any fields, then confirm to create the product and mark the request as Completed. If you adjust any core fields, the requester is notified of the changes in the confirmation email.</li>
            <li><strong>Decline</strong> — Opens a modal to enter a decline reason. The request returns to Draft and the requester is notified by email with your reason.</li>
          </ul>
        </section>

        {/* ── Technical Overview ───────────────────────────────── */}
        <section id="tech" className="help-page__section">
          <h2>Technical Overview</h2>
          <p>
            LiftOff is a full-stack web application built for Guidewire to manage product launch
            and capability intake processes across SKI releases.
          </p>

          <h3>Technology Stack</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Layer</th><th>Technology</th><th>Notes</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>Frontend</strong></td><td>React 18</td><td>Functional components, hooks, React Router v5</td></tr>
              <tr><td><strong>HTTP Client</strong></td><td>Axios</td><td>Centralised API service layer with interceptors</td></tr>
              <tr><td><strong>Backend</strong></td><td>Node.js 18+ / Express.js</td><td>REST API, runs on port 3001</td></tr>
              <tr><td><strong>Database (dev)</strong></td><td>SQLite</td><td>File-based, no setup required — <code>database.sqlite</code></td></tr>
              <tr><td><strong>Database (prod)</strong></td><td>PostgreSQL 14+</td><td>Managed via Sequelize ORM with migrations</td></tr>
              <tr><td><strong>Authentication</strong></td><td>OKTA OAuth 2.0 / JWT</td><td>Bypassed in dev via <code>BYPASS_AUTH=true</code></td></tr>
              <tr><td><strong>Security</strong></td><td>Helmet.js, CORS, Rate Limiting</td><td>100 requests / 15 min; XSS and SQL injection protection</td></tr>
              <tr><td><strong>Backend Tests</strong></td><td>Jest + Supertest</td><td>110+ tests — unit (services) + integration (API endpoints)</td></tr>
              <tr><td><strong>Frontend Tests</strong></td><td>Jest + React Testing Library</td><td>Smoke tests across Dashboard, RequestsTable, ProductDetail, ProductLinkSummaryModal, ProductHierarchy, Reporting, IntakeFlow</td></tr>
            </tbody>
          </table>

          <h3>Key Features Implemented</h3>
          <div className="help-page__type-grid">
            <div className="help-page__type-card">
              <h4>Dashboard</h4>
              <p>Product-grouped requests with view/link/edit icons, column restructure, CSV import/export, New Product and New Request actions.</p>
            </div>
            <div className="help-page__type-card">
              <h4>Intake Forms</h4>
              <p>Multi-step wizard with conditional question logic, auto-save every 30s, and support for CuC, CIC, BC, GPR (subtypes), Pricing &amp; Packaging, Product Master Change, SalesOps.</p>
            </div>
            <div className="help-page__type-card">
              <h4>Product Hierarchy</h4>
              <p>3-panel UI for managing Package → Commercial → Technical product relationships with smart pre-selection from the dashboard.</p>
            </div>
            <div className="help-page__type-card">
              <h4>Reporting</h4>
              <p>5 view modes (By SKI Release, By Product, By Release Type, By Hierarchy, Grid View) with interactive network graph, dynamic filters, and CSV export.</p>
            </div>
            <div className="help-page__type-card">
              <h4>SKI Release Management</h4>
              <p>Create and edit SKI releases with 9 milestone dates (PA, GA, CUC, BC, CiC, SalesOps, Privacy, GTM/GPRs, PPP). Copy Verified CuC requests between releases.</p>
            </div>
            <div className="help-page__type-card">
              <h4>Notifications</h4>
              <p>Email notifications for request status changes. Configurable on/off toggle in Admin → System Settings without requiring a server restart.</p>
            </div>
            <div className="help-page__type-card">
              <h4>Product Change History</h4>
              <p>Full audit trail per product. The History tab on the Product Detail page shows field-level diffs — old value (red) vs new value (green), who changed it, and when.</p>
            </div>
          </div>

          <h3>Database Schema (Core Tables)</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Table</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              <tr><td><code>users</code></td><td>User profiles synchronised from OKTA; stores role (admin / developer / approver / user)</td></tr>
              <tr><td><code>products</code></td><td>Product catalog — name, code, type, release type, categorization, owner, status</td></tr>
              <tr><td><code>requests</code></td><td>Intake requests with auto-generated request numbers (REQ-YYYYMMDD-####)</td></tr>
              <tr><td><code>request_answers</code></td><td>Dynamic question/answer pairs stored as key-value records</td></tr>
              <tr><td><code>question_schema</code></td><td>Question definitions with conditional logic rules per request type</td></tr>
              <tr><td><code>ski_releases</code></td><td>SKI release versions with 9 milestone dates (PA, GA, CUC, BC, CiC, SalesOps, Privacy, GTM/GPRs, PPP)</td></tr>
              <tr><td><code>request_types</code></td><td>Request type definitions (CuC, GPR, CIC, BC, etc.)</td></tr>
              <tr><td><code>audit_log</code></td><td>Full change history with before/after values for compliance</td></tr>
              <tr><td><code>product_audit_log</code></td><td>Field-level change history for products — tracked on every save</td></tr>
              <tr><td><code>email_queue</code></td><td>Outbound email jobs — status, recipient, retry count, sent timestamp</td></tr>
              <tr><td><code>system_settings</code></td><td>Runtime configuration key/value pairs (e.g. <code>email_enabled</code>)</td></tr>
            </tbody>
          </table>

          <h3>Running Locally</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Service</th><th>Directory</th><th>Command</th><th>Port</th></tr>
            </thead>
            <tbody>
              <tr><td>Backend</td><td><code>backend/</code></td><td><code>npm run dev</code></td><td>3001</td></tr>
              <tr><td>Frontend</td><td><code>frontend/</code></td><td><code>npm start</code></td><td>3000</td></tr>
            </tbody>
          </table>
        </section>

        {/* ── Database Seeding ──────────────────────────────────── */}
        <section id="db-seeding" className="help-page__section">
          <h2>Database Seeding</h2>
          <p>
            The app uses <strong>Sequelize CLI</strong> to manage schema (migrations) and initial data (seeds).
            Migrations are always safe to re-run — Sequelize tracks them in <code>SequelizeMeta</code>.
            Seeds are run once on a fresh database; newer seeds use a <code>seed_tracking</code> table for idempotency.
          </p>

          <h3>Fresh Database Setup</h3>
          <p>Use this sequence when starting from a completely empty database (e.g. after dropping the SQLite file):</p>
          <ol>
            <li>Run migrations to create all tables: <code>npm run migrate</code></li>
            <li>Run all seed files to populate question schema, request types, and SKI releases: <code>npm run seed</code></li>
            <li>Log in as an Admin and go to the <strong>Admin</strong> page.</li>
            <li>Under <strong>Product Import</strong>, click <strong>Import Products</strong> to load products from the Products seeding CSV.</li>
            <li>Under <strong>Seed Data Management</strong>, click <strong>Load Seed Data</strong> to import CUC data and link products to SKI releases.</li>
          </ol>
          <p>
            Steps 4 and 5 must be run from the Admin screen after the database seed — they are not part of the automated
            seed process and must be triggered manually by an administrator each time the database is reset.
          </p>

          <h3>Reseed an Existing Database</h3>
          <p>
            Use <code>npm run reseed</code> to reset data without touching the schema.
            The script presents two modes at startup:
          </p>

          <h4>Mode 1 — Full Reseed</h4>
          <p>Wipes <strong>all</strong> data and re-runs every seed file. Use this when you need a completely clean system state.</p>
          <ol>
            <li><strong>Mode &amp; confirmation prompt</strong> — select <code>1</code> then type <code>yes</code>.</li>
            <li><strong>Auto backup</strong> — creates a timestamped backup in <code>backend/backups/</code>. The last 5 backups are kept automatically.</li>
            <li><strong>Clear all data</strong> — deletes rows from all tables in foreign-key-safe order: request answers, requests, product hierarchy links, product–request type relationships, product–SKI release relationships, products, question schema, users, SKI releases, request types, audit log, email queue, saved reports, and seed tracking.</li>
            <li><strong>Re-run all seeds</strong> — <code>npm run seed</code> runs every file in <code>backend/seeds/</code> in filename (date) order.</li>
            <li><strong>Consistency checks</strong> — verifies request type integrity and product–request relationships.</li>
            <li><strong>Smoke tests</strong> — runs the full smoke test suite against the freshly seeded database.</li>
          </ol>
          <p>
            After a full reseed, an administrator must re-run <strong>Import Products</strong> and
            <strong>Load CUC Seed Data</strong> from the <strong>Admin</strong> page to restore product and CUC data.
          </p>

          <h4>Mode 2 — Data Refresh</h4>
          <p>Clears <strong>products and requests only</strong>, leaving question schema, users, SKI releases, request types, and system settings intact. Use this when you want to reload product data without a full system reset.</p>
          <ol>
            <li><strong>Mode &amp; confirmation prompt</strong> — select <code>2</code> then type <code>yes</code>.</li>
            <li><strong>Auto backup</strong> — same as Full Reseed.</li>
            <li><strong>Clear product &amp; request data</strong> — deletes: request answers, requests, products, product hierarchy links, product–request type relationships, product–SKI release relationships, audit logs, email queue, and saved reports.</li>
          </ol>
          <p>
            After a data refresh, go to <strong>Admin → Seed Data → Import Products</strong> to reload the product catalog,
            then run <strong>Load CUC Seed Data</strong> if required.
          </p>

          <h3>Seed File Coverage</h3>
          <p>The following request types and their question schemas are covered by seed files and will be fully restored after a reseed:</p>
          <table className="help-page__table">
            <thead>
              <tr><th>Request Type</th><th>Type Code</th><th>Seed File</th></tr>
            </thead>
            <tbody>
              <tr><td>Capability Update Catalog</td><td><code>CUC</code></td><td><code>20250101000000-question-schema.js</code></td></tr>
              <tr><td>SalesOps Request</td><td><code>SOR</code></td><td><code>20250303000000-salesops-request-questions.js</code></td></tr>
              <tr><td>GPR (GA, Existing, Internal, Marketplace)</td><td><code>GPR_GA / GPR_EX / GPR_INT / GPR_MKT</code></td><td><code>20260311000001-gpr-enhancements-questions.js</code></td></tr>
              <tr><td>GPR — EA Service</td><td><code>GPR_EA</code></td><td><code>20260321000001-gpr-ea-runbook-full.js</code></td></tr>
              <tr><td>GPR — Runbook Changes</td><td><code>GPR_RUNBOOK</code></td><td><code>20260321000001-gpr-ea-runbook-full.js</code></td></tr>
              <tr><td>Breaking Change</td><td><code>BREAKING_CHANGE</code></td><td><code>20260310000001-breaking-change-questions.js</code></td></tr>
              <tr><td>Customer Impact Communication</td><td><code>CIC</code></td><td><code>20260310000002-cic-questions.js</code></td></tr>
              <tr><td>Product Packaging &amp; Pricing</td><td><code>PPP</code></td><td><code>20260314000001-ppp-questions.js</code></td></tr>
              <tr><td>Pricing &amp; Packaging</td><td><code>PRICING_PACKAGING</code></td><td><code>20260312000001-pricing-packaging-questions.js</code></td></tr>
              <tr><td>Privacy</td><td><code>PRIVACY</code></td><td><code>20260318000001-privacy-questions.js</code></td></tr>
              <tr><td>New Product Request</td><td><code>NEW_PRODUCT_REQUEST</code></td><td><code>20260402000001-add-new-product-request-questions.js</code></td></tr>
            </tbody>
          </table>

          <h3>How Seed Idempotency Works</h3>
          <p>
            Newer seed files use <code>SeedHelper</code> which records each run in the <code>seed_tracking</code> table.
            On a re-run, if the seed name is already recorded as <code>success</code>, the seed is skipped.
            The reseed script clears <code>seed_tracking</code> before running seeds, so all SeedHelper-based seeds
            execute fresh. Older plain seeds (without SeedHelper) always insert unconditionally — they work correctly
            because the reseed script clears the target tables first.
          </p>

          <h3>npm Scripts Reference</h3>
          <table className="help-page__table">
            <thead>
              <tr><th>Command</th><th>What it does</th></tr>
            </thead>
            <tbody>
              <tr><td><code>npm run migrate</code></td><td>Runs pending Sequelize migrations (schema changes). Safe to re-run — skips already-applied migrations.</td></tr>
              <tr><td><code>npm run seed</code></td><td>Runs all seed files in date order. Use on a freshly migrated database.</td></tr>
              <tr><td><code>npm run reseed</code></td><td>Interactive reseed with two modes: <strong>Full Reseed</strong> (wipes all data, re-runs seeds, smoke tests) or <strong>Data Refresh</strong> (clears products and requests only, leaves config data intact). Both modes create an automatic backup first.</td></tr>
              <tr><td><code>npm run migrate:undo</code></td><td>Rolls back the most recent migration.</td></tr>
            </tbody>
          </table>
        </section>

      </main>
    </div>
  );
};

export default Help;
